import React, { useState, useEffect } from 'react';
import {
  Users, UserPlus, Shield, Edit3, Trash2, Search,
  CheckCircle2, Building2, X, Eye, EyeOff, Loader2, Mail, AlertCircle, Save, Briefcase, Clock,
  ArrowRight, Filter, Tag, MoreHorizontal, LayoutGrid, List, ChevronDown, Plus, Layout, Ban, Play, ChevronRight
} from 'lucide-react';
import { UserRole, User, AccessProfile } from '../types';
import { supabase, extractErrorMessage, supabase_get_clients, supabase_create_profile, supabase_admin_invite_user, supabase_get_site_config, supabase_save_site_config } from '../services/supabaseService';
import { sendUserInvite } from '../services/notificationService';
import SearchableCombobox from './SearchableCombobox';
import {
  filterOrganizationsForActor,
  isClientScopedRole,
  isOrganizationAllowedForActor,
  isWerftoolsStaffRole,
} from '../utils/tenantAccess';

interface SystemUser {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  status: 'Active' | 'Invited' | 'Suspended' | 'Archived';
  client_id: string | null;
  client_name?: string;
  client_type?: string;
  client_industry?: string;
  client_logo?: string; // Added Logo
  tags?: string[];
  visibleTabs: string[];
  providers?: string[];
}



const AVAILABLE_PAGES = [
  { id: 'dashboard', label: 'Overzicht' },
  { id: 'pipeline', label: 'Pipeline' },
  { id: 'shortlist', label: 'Shortlist' },
  { id: 'workspace', label: 'Recruiter Hub' },
  { id: 'vacancies', label: 'Vacatures' },
  { id: 'vacancy_templates', label: 'Vacature-sjablonen' },
  { id: 'talentpool', label: 'Kandidaten' },
  { id: 'placements', label: 'Plaatsingen' },
  { id: 'reporting', label: 'Rapportage' },
  { id: 'crm', label: 'Relaties (CRM)' },
  { id: 'documentbuilder', label: 'CV Builder' },
  { id: 'docs', label: 'Documenten' },
  { id: 'cv_templates', label: 'CV Templates' },
  { id: 'engagement_center', label: 'Engagement' },
  { id: 'automation', label: 'Automatisering' },
  { id: 'clients', label: 'Klanten' },
  { id: 'careers_site', label: 'Site Editor' },
  { id: 'products', label: 'Marketplace' },
  { id: 'subscription', label: 'Governance' },
  { id: 'logs', label: 'Audit Logs' },
  { id: 'users', label: 'Gebruikers' }
];

const AVAILABLE_SETTINGS_TABS = [
  { id: 'settings_checklists', label: 'Checklists' },
  { id: 'settings_sections', label: 'Standaard Secties' },
  { id: 'settings_ai_matching', label: 'AI Matching' },
  { id: 'settings_ai_prompts', label: 'Prompt Instellingen' },
  { id: 'settings_bullhorn_integration', label: 'Bullhorn Integratie' },
  { id: 'settings_bullhorn_pipeline', label: 'Pipeline Sync' },
  { id: 'settings_word_templates', label: 'Word CV Templates' },
  { id: 'settings_email_templates', label: 'Mail Templates' },
  { id: 'settings_whatsapp_templates', label: 'App Templates' },
  { id: 'settings_candidate_sources', label: 'Kandidaat Bronnen' }
];

const UserManagement: React.FC<{ user: User | null; filterClientId?: string }> = ({ user, filterClientId }) => {
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    const handleNew = () => setShowAddModal(true);
    window.addEventListener('open-new-user', handleNew);
    
    // Initial load
    fetchData();
    loadConfigs();

    return () => window.removeEventListener('open-new-user', handleNew);
  }, [user]);

  const [selectedUser, setSelectedUser] = useState<SystemUser | null>(null);
  const [users, setUsers] = useState<SystemUser[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState<'ALL' | UserRole | 'Admin' | 'Recruiter'>('ALL');
  const [filterStatus, setFilterStatus] = useState<'ALL' | 'Active' | 'Invited' | 'Suspended' | 'Archived' | 'Ongekoppeld'>('ALL');
  const [showFilters, setShowFilters] = useState(false);

  // Deletion Confirmation State
  const [deleteConfirmationUser, setDeleteConfirmationUser] = useState<SystemUser | null>(null);
  const [deleteInput, setDeleteInput] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    role: UserRole.CLIENT,
    client_id: '',
    accessProfileId: '',
    visibleTabs: ['dashboard', 'pipeline', 'shortlist', 'workspace', 'vacancies', 'talentpool', 'crm', 'documentbuilder'] as string[]
  });

  // Profile Management State
  const [activeSubTab, setActiveSubTab] = useState<'users' | 'profiles'>('users');
  const [accessProfiles, setAccessProfiles] = useState<AccessProfile[]>([]);
  const [config, setConfig] = useState<any>(null);
  const [isSavingProfiles, setIsSavingProfiles] = useState(false);



  const loadConfigs = async () => {
    const siteConfig = await supabase_get_site_config();
    setConfig(siteConfig);
    if (siteConfig?.accessProfiles) {
      setAccessProfiles(siteConfig.accessProfiles);
    } else {
      // Defaults if empty
      setAccessProfiles([
        { id: '1', name: 'Recruiter (Standard)', role: UserRole.CLIENT_RECRUITER, visibleTabs: ['dashboard', 'vacancies', 'candidates', 'talentpool'], description: 'Standaard recruiter toegang' },
        { id: '2', name: 'Hiring Manager (Beperkt)', role: UserRole.VACANCY_REVIEWER, visibleTabs: ['vacancies', 'candidates'], description: 'Alleen inzicht in specifieke vacatures' },
        { id: '3', name: 'Beheerder', role: UserRole.CLIENT, visibleTabs: ['dashboard', 'vacancies', 'candidates', 'talentpool', 'users', 'settings', 'subscription'], description: 'Volledige toegang' }
      ]);
    }
  };

  const saveAccessProfiles = async () => {
    setIsSavingProfiles(true);
    try {
      const newConfig = { ...config, accessProfiles };
      await supabase_save_site_config(newConfig);
      alert("Profielen opgeslagen!");
    } catch (e) {
      console.error("Error saving profiles", e);
      alert("Fout bij opslaan profielen.");
    } finally {
      setIsSavingProfiles(false);
    }
  };

  const addProfile = () => {
    const newProfile: AccessProfile = {
      id: crypto.randomUUID(),
      name: 'Nieuw Profiel',
      role: UserRole.CLIENT_RECRUITER,
      visibleTabs: ['dashboard'],
      description: ''
    };
    setAccessProfiles([...accessProfiles, newProfile]);
  };

  const removeProfile = (id: string) => {
    if (confirm("Weet je zeker dat je dit profiel wilt verwijderen?")) {
      setAccessProfiles(accessProfiles.filter(p => p.id !== id));
    }
  };

  const updateProfile = (id: string, updates: Partial<AccessProfile>) => {
    setAccessProfiles(accessProfiles.map(p => p.id === id ? { ...p, ...updates } : p));
  };

  const toggleProfileTab = (profileId: string, tab: string) => {
    const profile = accessProfiles.find(p => p.id === profileId);
    if (!profile) return;
    const newTabs = profile.visibleTabs.includes(tab)
      ? profile.visibleTabs.filter(t => t !== tab)
      : [...profile.visibleTabs, tab];
    updateProfile(profileId, { visibleTabs: newTabs });
  };

  const fetchData = async () => {
    setIsLoading(true);
    // Safety timer
    const safetyTimer = setTimeout(() => {
      if (isLoading) {
        console.warn("UserManagement fetch timed out");
        setIsLoading(false);
      }
    }, 10000);

    try {
      if (!user) {
        clearTimeout(safetyTimer);
        setIsLoading(false);
        return;
      }

      let query = supabase.from('profiles').select('*');
      if (filterClientId) {
        query = query.eq('client_id', filterClientId);
      } else if (user.role !== UserRole.ADMIN && user.clientId) {
        query = query.eq('client_id', user.clientId);
      }

      console.log("[UserManagement] Fetching data...");
      // Parallel fetch to speed up
      const clientsScopeId =
        filterClientId ||
        (isWerftoolsStaffRole(user.role) ? undefined : user.clientId || undefined);
      const [clientList, profilesResult] = await Promise.all([
        supabase_get_clients(clientsScopeId),
        query,
      ]);

      console.log("[UserManagement] Clients:", clientList?.length);
      console.log("[UserManagement] Profiles:", profilesResult.data?.length, "Error:", profilesResult.error);

      setClients(clientList || []);

      const { data: profiles, error: profileError } = profilesResult;

      if (profileError) {
        console.error("[UserManagement] Profile fetch error", profileError);
        // Do not throw yet, try fallback
      }

      let finalProfiles = profiles || [];

      // If standard fetch yields nothing, or if we want to enrich with Auth Providers for Admins
      let adminUsers: any[] = [];
      if (user.role === UserRole.ADMIN) {
        try {
          const { supabase_admin_list_users } = await import('../services/supabaseService');
          adminUsers = await supabase_admin_list_users();
          
          if (finalProfiles.length === 0 && adminUsers && adminUsers.length > 0) {
            console.log("[UserManagement] Standard fetch empty. Using Admin API fallback...");
            finalProfiles = adminUsers.map((u: any) => ({
              id: u.id,
              name: u.user_metadata?.name || u.email?.split('@')[0] || 'Unknown',
              email: u.email,
              role: u.user_metadata?.role || UserRole.CLIENT,
              status: u.user_metadata?.isArchived ? 'Archived' : (u.banned_until ? 'Suspended' : (u.confirmed_at ? 'Active' : 'Invited')),
              client_id: u.user_metadata?.client_id || null,
              visible_tabs: u.user_metadata?.visible_tabs || []
            }));
          }
        } catch (adminErr) {
          console.error("Admin Users fetch failed:", adminErr);
        }
      }

      if (finalProfiles.length > 0) {
        // Manually join clients in memory
        const clientMap = new Map(clientList?.map((c: any) => [c.id, c]) || []);
        
        // Build Auth Map to grab providers
        const authMap = new Map(adminUsers.map(u => [u.email, u]));

        setUsers(finalProfiles.map((p: any) => {
          const client = p.client_id ? clientMap.get(p.client_id) : null;
          const safeClient = client as any; // Cast to any to fix TS errors
          const authUser = p.email ? authMap.get(p.email) : null;

          return {
            id: p.id,
            name: p.name,
            email: p.email,
            role: p.role,
            status: p.status || 'Active',
            client_id: p.client_id,
            client_name: safeClient?.name,
            client_type: safeClient?.client_type,
            client_industry: safeClient?.industry,
            client_logo: safeClient?.logo_url,
            tags: [
              safeClient?.client_type === 'Agency' ? 'Partner' : 'Direct',
              safeClient?.industry,
              p.role === UserRole.ADMIN ? 'Admin' : null
            ].filter(Boolean) as string[],
            visibleTabs: p.visible_tabs || [],
            providers: authUser?.app_metadata?.providers || []
          };
        }));
      } else {
        setUsers([]);
      }
    } catch (err: any) {
      console.warn("Fetch users error:", err.message);
      setUsers([]);
    } finally {
      clearTimeout(safetyTimer);
      setIsLoading(false);
    }
  };

  const assignableOrganizations = React.useMemo(
    () => filterOrganizationsForActor(clients, user || {}),
    [clients, user],
  );

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsProcessing(true);
    setError(null);

    try {
      let payloadClientId = newUser.client_id === '' ? null : newUser.client_id;

      if (user && isClientScopedRole(user.role)) {
        if (editingUserId === user.id && payloadClientId && payloadClientId !== user.clientId) {
          throw new Error('Je kunt je eigen organisatie niet wijzigen.');
        }
        if (payloadClientId && !isOrganizationAllowedForActor(payloadClientId, user, clients)) {
          throw new Error('Geen toegang tot deze organisatie.');
        }
        if (!payloadClientId && user.clientId) {
          payloadClientId = user.clientId;
        }
      }

      // Map VACANCY_REVIEWER to CLIENT_RECRUITER for DB compatibility
      let dbRole = newUser.role;
      // We do NOT override visibleTabs here anymore, treating user selection as source of truth.
      // The user wants "module toegang" to work.

      if (newUser.role === UserRole.VACANCY_REVIEWER) {
        dbRole = UserRole.CLIENT_RECRUITER;
      }

      // Safety: Ensure visible_tabs is an array, never null/undefined
      const safeVisibleTabs = newUser.visibleTabs || [];

      if (editingUserId) {
        const apiUrl = window.location.hostname === 'localhost' ? '/api/users' : '/api/users';
        const response = await fetch(`${apiUrl}/${editingUserId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: newUser.name,
            role: dbRole,
            client_id: payloadClientId,
            visible_tabs: safeVisibleTabs
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Failed to update user");
        }

        setSuccessMessage(`Updated ${newUser.name}.`);
        fetchData(); // Refresh to ensure sync
      } else {
        // 1. Invite and Create Profile via Node Backend Admin API
        const apiUrl = window.location.hostname === 'localhost' ? '/api/users' : '/api/users';

        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: newUser.email,
            role: dbRole,
            name: newUser.name,
            client_id: payloadClientId,
            visible_tabs: safeVisibleTabs
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Failed to create user");
        }

        // 2. Send Notification (Simulated)
        try {
          await sendUserInvite({
            to: newUser.email,
            name: newUser.name,
            role: newUser.role // Send original role in email for clarity
          });
        } catch (inviteErr) {
          console.warn("Invite sending failed:", inviteErr);
        }

        setSuccessMessage(`Invite sent to ${newUser.email}`);
        fetchData();
      }

      setShowAddModal(false);
      setTimeout(() => setSuccessMessage(null), 5000);

    } catch (err: any) {
      console.error("Add user error:", err);
      setError(extractErrorMessage(err));
    } finally {
      setIsProcessing(false);
    }
  };

  const startEdit = (user: SystemUser) => {
    setError(null);
    setEditingUserId(user.id);
    setNewUser({
      name: user.name,
      email: user.email,
      role: user.role,
      client_id: user.client_id || '',
      accessProfileId: '', // Default to empty as we don't store it on user yet
      visibleTabs: user.visibleTabs || []
    });
    setShowAddModal(true);
  };

  const toggleTabPermission = (tabId: string) => {
    setNewUser(prev => {
      const tabs = prev.visibleTabs || [];
      return {
        ...prev,
        visibleTabs: tabs.includes(tabId)
          ? tabs.filter(id => id !== tabId)
          : [...tabs, tabId]
      };
    });
  };

  /* PAGINATION LOGIC */
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8; // More compact, fewer items per page

  const filteredUsers = users.filter(u => {
    const matchesSearch = u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (u.client_name && u.client_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (u.tags?.some(t => t.toLowerCase().includes(searchTerm.toLowerCase())));

    let matchesRole = true;
    if (filterRole !== 'ALL') {
      if (filterRole === 'Admin') matchesRole = u.role === UserRole.ADMIN || u.role === UserRole.CLIENT;
      else if (filterRole === 'Recruiter') matchesRole = u.role === UserRole.RECRUITER || u.role === UserRole.CLIENT_RECRUITER;
      else matchesRole = u.role === filterRole;
    }

    let matchesStatus = filterStatus === 'ALL' || u.status === filterStatus;
    // Special case for Ongekoppeld: when client_id is specifically missing
    if (filterStatus === 'Ongekoppeld') {
      matchesStatus = !u.client_id;
    }

    return matchesSearch && matchesRole && matchesStatus;
  });

  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const paginatedUsers = filteredUsers.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const getInitials = (name: string) => name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();

  const handleLoginAs = async (targetUser: SystemUser) => {
    if (!config?.accessProfiles && !confirm(`Wil je inloggen als ${targetUser.name}? Je huidige sessie wordt beëindigd.`)) return;

    try {
      // Dynamic import to avoid circular dependency if any
      const { supabase_admin_generate_link } = await import('../services/supabaseService');
      const link = await supabase_admin_generate_link(targetUser.email);

      if (link) {
        await supabase.auth.signOut();
        window.location.href = link;
      } else {
        alert("Kon geen inloglink genereren. (Is ADMIN Key ingesteld?)");
      }
    } catch (e) {
      console.error(e);
      alert("Fout bij genereren link.");
    }
  };

  const handleInitiateDelete = (targetUser: SystemUser) => {
    setDeleteConfirmationUser(targetUser);
    setDeleteInput('');
  };

  const confirmDelete = async () => {
    if (!deleteConfirmationUser) return;
    const firstName = deleteConfirmationUser.name.split(' ')[0];
    if (deleteInput.trim().toLowerCase() !== firstName.toLowerCase()) return;
    
    setIsDeleting(true);
    try {
      const { supabase_admin_archive_user } = await import('../services/supabaseService');
      await supabase_admin_archive_user(deleteConfirmationUser.id);
      
      setSuccessMessage(`${deleteConfirmationUser.name} is succesvol gearchiveerd.`);
      if (selectedUser?.id === deleteConfirmationUser.id) setSelectedUser(null);
      fetchData();
      setTimeout(() => setSuccessMessage(null), 3000);
      setDeleteConfirmationUser(null);
    } catch (e: any) {
      if (e.message?.includes('Database error') || e.message?.includes('foreign key')) {
          alert("Fout bij archiveren: Kan deze gebruiker niet archiveren. " + e.message);
      } else {
          alert("Fout bij archiveren: " + e.message);
      }
    } finally {
      setIsDeleting(false);
      setDeleteInput('');
    }
  };

  const handleToggleSuspend = async (targetUser: SystemUser) => {
    const isSuspended = targetUser.status === 'Suspended';
    const isArchived = targetUser.status === 'Archived';
    
    if (isArchived) {
      if (!confirm(`Weet je zeker dat je het account van [${targetUser.name}] wilt DE-archiveren en opnieuw activeren?`)) return;
      try {
        const { supabase_admin_unarchive_user } = await import('../services/supabaseService');
        await supabase_admin_unarchive_user(targetUser.id);
        setSuccessMessage(`${targetUser.name} is succesvol gede-archiveerd en weer actief.`);
        if (selectedUser?.id === targetUser.id) setSelectedUser(u => u ? { ...u, status: 'Active' } : null);
        fetchData();
        setTimeout(() => setSuccessMessage(null), 3000);
      } catch (e: any) {
        alert("Fout bij de-archiveren: " + e.message);
      }
      return;
    }

    const actionStr = isSuspended ? 'activeren' : 'pauzeren (blokkeren)';
    if (!confirm(`Weet je zeker dat je toegang voor [${targetUser.name}] wilt ${actionStr}?`)) return;

    try {
      const { supabase_admin_suspend_user } = await import('../services/supabaseService');
      await supabase_admin_suspend_user(targetUser.id, !isSuspended);
      setSuccessMessage(`${targetUser.name} is nu ${isSuspended ? 'geactiveerd' : 'geblokkeerd'}.`);
      if (selectedUser?.id === targetUser.id) setSelectedUser(u => u ? { ...u, status: isSuspended ? 'Active' : 'Suspended' } : null);
      fetchData();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (e: any) {
      alert("Fout bij wijzigen status: " + e.message);
    }
  };

  return (
    <div className={`h-full flex flex-col bg-white font-sans ${filterClientId ? 'p-0' : ''}`}>
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
        {!filterClientId && (
          <div>
            <h1 className="text-xl font-black text-slate-900 tracking-tight leading-tight flex items-center gap-2">
              <Users className="text-[#e51b23] shrink-0" size={24} />
              Klanten & Gebruikers
            </h1>
            <p className="text-xs text-slate-500 font-medium mt-1">Beheer accounts, facturatiegegevens en contactinformatie.</p>
          </div>
        )}
        <div className="flex items-center gap-3">
          {successMessage && (
            <div className="px-4 py-2 bg-emerald-50 text-emerald-600 rounded-lg border border-emerald-100 flex items-center gap-2 text-xs font-bold uppercase tracking-wide animate-slideDown">
              <CheckCircle2 size={14} /> {successMessage}
            </div>
          )}
          <button
            onClick={() => {
              setError(null);
              setEditingUserId(null);
              setNewUser({
                name: '',
                email: '',
                role: user?.role === UserRole.ADMIN ? UserRole.CLIENT : UserRole.CLIENT_RECRUITER,
                client_id: filterClientId || user?.clientId || (clients.length === 1 ? clients[0].id : ''),
                accessProfileId: '',
                visibleTabs: ['dashboard', 'vacancies', 'documentbuilder']
              });
              setShowAddModal(true);
            }}
            className="flex items-center gap-2 bg-slate-900 text-white px-5 py-2.5 rounded-lg font-black text-xs uppercase tracking-widest shadow-lg hover:bg-black active:scale-95 transition-all"
          >
            <UserPlus size={16} /> Gebruiker Toevoegen
          </button>
        </div>
      </div>

      <div className="bg-white border-b border-slate-200 px-6 py-3 shrink-0">
      <div className="flex items-center gap-1 bg-slate-100/50 p-1 rounded-xl w-fit">
        <button
          onClick={() => setActiveSubTab('users')}
          className={`px-4 py-2 rounded-lg text-xs font-black uppercase tracking-widest transition-all ${activeSubTab === 'users' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
        >
          Gebruikers
        </button>
        <button
          onClick={() => { alert("Manual Refresh Triggered"); fetchData(); }}
          className="px-4 py-2 bg-red-100 text-red-600 rounded-lg text-xs font-bold uppercase tracking-widest hover:bg-red-200"
        >
          DEBUG REFRESH
        </button>
        {(user?.role === UserRole.ADMIN || user?.role === UserRole.CLIENT) && (
          <button
            onClick={() => setActiveSubTab('profiles')}
            className={`px-4 py-2 rounded-lg text-xs font-black uppercase tracking-widest transition-all ${activeSubTab === 'profiles' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
          >
            Toegangsprofielen
          </button>
        )}
      </div>
      </div>

      <div className="flex-1 overflow-auto px-6 py-4">
        {activeSubTab === 'users' && users.some(u => !u.client_id) && (
          <div className="bg-red-50 border-2 border-red-500 rounded-2xl p-4 flex items-center justify-between shadow-sm animate-pulse-slow mb-4">
            <div className="flex items-center gap-3 text-red-700">
              <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                <AlertCircle size={24} className="text-red-600" />
              </div>
              <div>
                <h3 className="font-black tracking-tight text-sm">Nieuwe gebruiker(s) in de wachtrij!</h3>
                <p className="text-xs font-medium text-red-600">Er zijn {users.filter(u => !u.client_id).length} gebruiker(s) zonder bedrijfsaccount. Koppel ze om ze toegang te verlenen.</p>
              </div>
            </div>
            <button
              onClick={() => setFilterStatus('Ongekoppeld')}
              className="px-4 py-2 bg-red-600 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-red-700 transition-colors shadow-lg active:scale-95"
            >
              Toon Gebruikers
            </button>
          </div>
        )}

      {activeSubTab === 'users' ? (
        <>
          {/* Filter Bar */}
          <div className="bg-white border border-slate-200 rounded-xl flex flex-col md:flex-row gap-2 mb-4 overflow-hidden">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <input
                type="text"
                placeholder="Zoek op naam, bedrijf, tag..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                className="w-full pl-11 pr-4 py-3 bg-transparent text-sm font-bold text-slate-900 outline-none placeholder:text-slate-400"
              />
            </div>

            <div className="flex items-center gap-2 px-2 border-l border-slate-100 pl-4">
              <div className="flex gap-2">
                {['ALL', 'Active', 'Invited', 'Suspended', 'Archived', 'Ongekoppeld'].map(status => (
                  <button
                    key={status}
                    onClick={() => setFilterStatus(status as any)}
                    className={`relative px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-widest transition-colors ${filterStatus === status ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
                  >
                    {status === 'Ongekoppeld' && users.some(u => !u.client_id) && (
                      <span className="absolute -top-1 -right-1 flex h-3 w-3">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500 border-2 border-white"></span>
                      </span>
                    )}
                    {status === 'ALL' ? 'Alle Statussen' : status}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Redesigned Clean Table */}
          {isLoading ? (
            <div className="py-20 flex flex-col items-center justify-center">
              <Loader2 className="animate-spin text-slate-900 mb-4" size={32} />
              <p className="text-xs font-black uppercase tracking-widest text-slate-300">Gebruikers laden...</p>
            </div>
          ) : users.length === 0 ? (
            <div className="py-20 flex flex-col items-center justify-center border-2 border-dashed border-slate-200 rounded-[2rem] bg-slate-50/50">
              <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mb-4 text-slate-300 shadow-sm">
                <Users size={24} />
              </div>
              <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Nog geen gebruikers gevonden.</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead className="sticky top-0 z-10">
                    <tr className="bg-slate-50 border-b border-slate-200 text-slate-500">
                      <th className="px-6 py-3 text-[10px] font-black uppercase tracking-widest w-[35%]">Gebruiker & Organisatie</th>
                      <th className="px-6 py-3 text-[10px] font-black uppercase tracking-widest">Rol & Status</th>
                      <th className="px-6 py-3 text-[10px] font-black uppercase tracking-widest">Tags</th>
                      <th className="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-right">Acties</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {paginatedUsers.map((u) => (
                      <tr key={u.id} onClick={() => setSelectedUser(u)} className="hover:bg-slate-50/80 transition-colors group cursor-pointer">
                        <td className="px-6 py-3">
                          <div className="flex items-center gap-4">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-xs font-black border shadow-sm overflow-hidden ${u.status === 'Active' ? 'bg-white border-slate-100 text-slate-700' : 'bg-slate-50 border-slate-100 text-slate-300'
                              }`}>
                              {u.client_logo ? (
                                <img src={u.client_logo} alt={u.client_name} className="w-full h-full object-contain p-1.5" />
                              ) : (
                                u.client_name ? u.client_name.charAt(0) : getInitials(u.name)
                              )}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <h4 className="text-sm font-bold text-slate-900">{u.name}</h4>
                                {!filterClientId && u.client_name && (
                                  <span className="flex items-center gap-1 text-[9px] text-slate-500 font-bold uppercase tracking-wide bg-slate-100 px-1.5 py-0.5 rounded-md">
                                    <Building2 size={10} /> {u.client_name}
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-slate-500 font-medium mt-0.5">{u.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-3">
                          <div className="flex flex-col gap-1.5 items-start">
                            <span className="px-2 py-0.5 bg-slate-900 text-white rounded-md text-[9px] font-black uppercase tracking-wide">
                              {u.role === 'CLIENT_RECRUITER' ? 'RECRUITER' 
                                : u.role === 'VACANCY_REVIEWER' ? 'REVIEWER' 
                                : u.role === 'CLIENT' ? 'ADMIN' 
                                : u.role.replace('CLIENT_', '')
                              }
                            </span>
                            
                            {/* SSO Providers */}
                            {u.providers && u.providers.length > 0 && (
                              <div className="flex items-center gap-1 mt-0.5">
                                {u.providers.includes('azure') && (
                                   <span className="flex items-center gap-1 text-[8px] font-bold uppercase tracking-widest text-[#00a4ef] bg-[#00a4ef]/10 px-1.5 py-0.5 rounded">Microsoft</span>
                                )}
                                {u.providers.includes('google') && (
                                   <span className="flex items-center gap-1 text-[8px] font-bold uppercase tracking-widest text-[#ea4335] bg-[#ea4335]/10 px-1.5 py-0.5 rounded">Google</span>
                                )}
                                {u.providers.includes('email') && (
                                   <span className="flex items-center gap-1 text-[8px] font-bold uppercase tracking-widest text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">Email</span>
                                )}
                              </div>
                            )}

                            <div className={`flex items-center gap-1.5 text-[10px] mt-0.5 font-bold uppercase tracking-wide ${u.status === 'Active' ? 'text-emerald-600' : 'text-slate-400'
                              }`}>
                              <div className={`w-1.5 h-1.5 rounded-full ${u.status === 'Active' ? 'bg-emerald-500' : 'bg-slate-300'}`}></div>
                              {u.status}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-3">
                          <div className="flex flex-wrap gap-2">
                            {u.tags?.map((tag, idx) => (
                              <span key={idx} className="flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-600 border border-slate-200 rounded-lg text-[9px] font-bold uppercase tracking-tight">
                                <Tag size={10} /> {tag}
                              </span>
                            ))}
                            {(!u.tags || u.tags.length === 0) && <span className="text-slate-300 text-[10px]">-</span>}
                          </div>
                        </td>
                        <td className="px-6 py-3 text-right">
                          <div className="flex items-center justify-end gap-1 transition-all duration-300">
                            <button className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 text-slate-500 group-hover:bg-slate-900 group-hover:text-white rounded-lg text-[10px] font-black uppercase tracking-widest transition-all shadow-sm">
                              Details <ChevronRight size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/50 flex items-center justify-between">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                  Pagina {currentPage} van {totalPages}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="w-8 h-8 flex items-center justify-center bg-white border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-all"
                  >
                    <ArrowRight size={14} className="rotate-180" />
                  </button>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="w-8 h-8 flex items-center justify-center bg-white border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-all"
                  >
                    <ArrowRight size={14} />
                  </button>
                </div>
              </div>
            </div>
          )}

          {showAddModal && (
            <div className="fixed inset-0 z-[200] flex items-center justify-center p-6 bg-slate-900/40 backdrop-blur-sm animate-fadeIn">
              <div className="bg-white w-full max-w-2xl rounded-[2rem] shadow-2xl overflow-hidden p-8 animate-scaleIn">
                <div className="flex items-center justify-between mb-8 pb-4 border-b border-slate-100">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-slate-900 text-white rounded-2xl flex items-center justify-center shadow-lg">
                      <UserPlus size={24} />
                    </div>
                    <div>
                      <h2 className="text-xl font-black text-slate-900 uppercase tracking-tight">{editingUserId ? 'Bewerk Account' : 'Nodig Klant Uit'}</h2>
                      <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Toegangsbeheer</p>
                    </div>
                  </div>
                  <button onClick={() => setShowAddModal(false)} className="p-2 text-slate-400 hover:text-red-600 rounded-full transition-colors hover:bg-red-50">
                    <X size={24} />
                  </button>
                </div>

                {error && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-xl text-red-600 text-xs font-bold flex items-center gap-3">
                    <AlertCircle size={18} /> {error}
                  </div>
                )}

                <form onSubmit={handleAddUser} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-1">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">Volledige Naam</label>
                      <input
                        type="text"
                        required
                        placeholder="e.g. Marieke Jansen"
                        value={newUser.name}
                        onChange={e => setNewUser({ ...newUser, name: e.target.value })}
                        className="w-full p-3 bg-slate-50 border-0 rounded-xl text-sm font-bold outline-none focus:ring-2 focus:ring-slate-900 transition-all text-slate-900"
                      />
                    </div>

                    {!filterClientId && (
                      <div className="space-y-1">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">Organisatie</label>
                        {user && editingUserId === user.id && isClientScopedRole(user.role) ? (
                          <div className="w-full p-3 bg-slate-100 border border-slate-200 rounded-xl text-sm font-bold text-slate-700">
                            {assignableOrganizations.find((c) => c.id === user.clientId)?.name ||
                              user.clientId ||
                              '—'}
                            <p className="text-[10px] font-medium text-slate-400 mt-1 normal-case tracking-normal">
                              Je organisatie is gekoppeld aan je account en kan hier niet worden gewijzigd.
                            </p>
                          </div>
                        ) : (
                          <div className="relative">
                            <SearchableCombobox
                              options={assignableOrganizations.map((c) => ({
                                id: c.id,
                                title: c.name,
                                subtitle: c.parent_client_id
                                  ? 'Werkplek'
                                  : c.client_type === 'Agency'
                                    ? 'Partner Bureau'
                                    : 'Directe Klant',
                                badge: c.parent_client_id ? 'Werkplek' : 'Toolklant',
                              }))}
                              value={newUser.client_id || ''}
                              onChange={(val) => setNewUser({ ...newUser, client_id: val })}
                              placeholder="-- Selecteer Organisatie --"
                              searchPlaceholder={
                                isWerftoolsStaffRole(user?.role)
                                  ? 'Zoek een Toolklant...'
                                  : 'Zoek je organisatie of werkplek...'
                              }
                              icon={Building2}
                              emptyText="Geen organisaties gevonden."
                            />
                          </div>
                        )}
                      </div>
                    )}

                    <div className="space-y-1">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">Email Adres</label>
                      <input
                        type="email"
                        required
                        disabled={!!editingUserId}
                        placeholder="name@company.com"
                        value={newUser.email}
                        onChange={e => setNewUser({ ...newUser, email: e.target.value })}
                        className="w-full p-3 bg-slate-50 border-0 rounded-xl text-sm font-bold outline-none focus:ring-2 focus:ring-slate-900 disabled:opacity-50 text-slate-900"
                      />
                    </div>

                    {/* Access Profile Selection */}
                    {accessProfiles.length > 0 && (
                      <div className="space-y-1">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">Toegangsprofiel (Optioneel)</label>
                        <div className="relative">
                          <Shield className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                          <select
                            value={newUser.accessProfileId || ''}
                            onChange={e => {
                              const profileId = e.target.value;
                              const profile = accessProfiles.find(p => p.id === profileId);
                              setNewUser(prev => ({
                                ...prev,
                                accessProfileId: profileId,
                                // If profile selected, auto-set role and tabs
                                ...(profile ? { role: profile.role, visibleTabs: profile.visibleTabs } : {})
                              }));
                            }}
                            className="w-full pl-12 pr-4 py-3 bg-slate-50 border-0 rounded-xl text-sm font-bold text-slate-900 outline-none focus:ring-2 focus:ring-slate-900 transition-all appearance-none text-slate-900 cursor-pointer"
                          >
                            <option value="">Geen profiel (Handmatig instellen)</option>
                            {accessProfiles.map(p => (
                              <option key={p.id} value={p.id}>{p.name}</option>
                            ))}
                          </select>
                          <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={16} />
                        </div>
                      </div>
                    )}

                    <div className="space-y-1">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">Systeem Rol</label>
                      <div className="relative">
                        <select
                          value={newUser.role}
                          onChange={e => setNewUser({ ...newUser, role: e.target.value as UserRole })}
                          className="w-full p-3 pr-10 bg-slate-50 border-0 rounded-xl text-sm font-bold outline-none cursor-pointer focus:ring-2 focus:ring-slate-900 appearance-none text-slate-900"
                        >
                          {user?.role === UserRole.ADMIN ? (
                            <>
                              <option value={UserRole.ADMIN}>System Admin</option>
                              <option value={UserRole.RECRUITER}>Werftools Recruiter</option>
                            </>
                          ) : (
                            // If not admin but the user being edited IS an admin/recruiter, show their current role so it doesn't silently change
                            (newUser.role === UserRole.ADMIN || newUser.role === UserRole.RECRUITER) && (
                              <option value={newUser.role} disabled>
                                {newUser.role === UserRole.ADMIN ? 'System Admin' : 'Werftools Recruiter'} (Niet wijzigbaar)
                              </option>
                            )
                          )}
                          <option value={UserRole.CLIENT}>Client Admin</option>
                          <option value={UserRole.CLIENT_RECRUITER}>Client Recruiter</option>
                          <option value={UserRole.VACANCY_REVIEWER}>Vacancy Reviewer (Alleen Beoordelen)</option>
                        </select>
                        <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={16} />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-3">Module Toegang</label>
                    <div className="grid grid-cols-2 gap-3 bg-slate-50 p-4 rounded-xl border border-slate-100 max-h-[180px] overflow-y-auto custom-scrollbar">
                      {AVAILABLE_PAGES.filter(p => user?.role === UserRole.ADMIN || !['clients', 'logs'].includes(p.id)).map(page => (
                        <button
                          key={page.id}
                          type="button"
                          onClick={() => toggleTabPermission(page.id)}
                          className={`flex items-center gap-3 px-4 py-2.5 rounded-lg text-[10px] font-black uppercase tracking-tight transition-all text-left ${newUser.visibleTabs.includes(page.id)
                            ? 'bg-slate-900 text-white shadow-md'
                            : 'bg-white text-slate-400 border border-slate-200 hover:border-slate-300'
                            }`}
                        >
                          {newUser.visibleTabs.includes(page.id) ? <Eye size={12} /> : <EyeOff size={12} />}
                          {page.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-3">Instellingen Toegang</label>
                    <div className="grid grid-cols-2 gap-3 bg-slate-50 p-4 rounded-xl border border-slate-100 max-h-[180px] overflow-y-auto custom-scrollbar">
                      {AVAILABLE_SETTINGS_TABS.map(page => (
                        <button
                          key={page.id}
                          type="button"
                          onClick={() => toggleTabPermission(page.id)}
                          className={`flex items-center gap-3 px-4 py-2.5 rounded-lg text-[10px] font-black uppercase tracking-tight transition-all text-left ${newUser.visibleTabs.includes(page.id)
                            ? 'bg-slate-900 text-white shadow-md'
                            : 'bg-white text-slate-400 border border-slate-200 hover:border-slate-300'
                            }`}
                        >
                          {newUser.visibleTabs.includes(page.id) ? <Eye size={12} /> : <EyeOff size={12} />}
                          {page.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="pt-4 border-t border-slate-100 flex justify-end">
                    <button
                      type="submit"
                      disabled={isProcessing}
                      className="px-10 py-4 bg-[#e51b23] text-white rounded-xl font-black text-xs uppercase tracking-widest shadow-xl hover:bg-black flex items-center justify-center gap-2 active:scale-95 disabled:opacity-50 transition-all"
                    >
                      {isProcessing ? <Loader2 size={16} className="animate-spin" /> : (editingUserId ? <Save size={16} /> : <Mail size={16} />)}
                      {editingUserId ? 'Account Updaten' : 'Verstuur Uitnodiging'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )
          }
        </>
      ) : (
        <div className="max-w-5xl mx-auto animate-fadeIn">
          {/* Header for Profiles */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-xl font-black text-slate-900 uppercase tracking-tight">Beheer Toegangsprofielen</h2>
              <p className="text-slate-500 font-medium text-xs mt-1">Definieer rollen en zichtbare modules voor gebruikers.</p>
            </div>
            <button
              onClick={saveAccessProfiles}
              disabled={isSavingProfiles}
              className="bg-[#e51b23] text-white px-5 py-2.5 rounded-xl font-black text-xs uppercase tracking-widest shadow-lg hover:bg-black transition-all flex items-center gap-2 active:scale-95 disabled:opacity-50"
            >
              {isSavingProfiles ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Opslaan
            </button>
          </div>

          <div className="space-y-4">
            {accessProfiles.map((profile, index) => (
              <div key={profile.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden group">
                <div className="p-4 border-b border-slate-100 bg-slate-50/50 flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-8 h-8 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-slate-400 font-bold text-xs">
                      {index + 1}
                    </div>
                    <div>
                      <input
                        type="text"
                        value={profile.name}
                        onChange={(e) => updateProfile(profile.id, { name: e.target.value })}
                        className="bg-transparent border-none text-sm font-black text-slate-900 uppercase tracking-tight focus:ring-0 p-0 placeholder-slate-300 w-full"
                        placeholder="Profiel Naam"
                      />
                      <input
                        type="text"
                        value={profile.description || ''}
                        onChange={(e) => updateProfile(profile.id, { description: e.target.value })}
                        className="bg-transparent border-none text-[10px] font-bold text-slate-400 uppercase tracking-wide focus:ring-0 p-0 w-full mt-0.5"
                        placeholder="Omschrijving..."
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <select
                      value={profile.role}
                      onChange={(e) => updateProfile(profile.id, { role: e.target.value as UserRole })}
                      className="bg-white border border-slate-200 text-slate-600 text-[10px] font-bold uppercase tracking-wide rounded-lg px-2 py-1.5 outline-none focus:border-slate-400"
                    >
                      {Object.values(UserRole).map(role => (
                        <option key={role} value={role}>{role}</option>
                      ))}
                    </select>
                    <button
                      onClick={() => removeProfile(profile.id)}
                      className="p-2 text-slate-300 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                <div className="p-4">
                  <h4 className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                    <Layout size={10} /> Toegankelijke Modules
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {AVAILABLE_PAGES.map(tab => {
                      const isSelected = profile.visibleTabs.includes(tab.id);
                      return (
                        <button
                          key={tab.id}
                          onClick={() => toggleProfileTab(profile.id, tab.id)}
                          className={`p-2.5 rounded-xl border text-left transition-all relative overflow-hidden ${isSelected ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-white border-slate-100 text-slate-400 hover:border-slate-200'}`}
                        >
                          <div className="relative z-10 flex items-center justify-between">
                            <span className="text-[9px] font-black uppercase tracking-widest">{tab.label}</span>
                            {isSelected && <CheckCircle2 size={12} className="text-emerald-500" />}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            ))}

            <button
              onClick={addProfile}
              className="w-full py-3 rounded-2xl border-2 border-dashed border-slate-200 text-slate-400 font-black text-xs uppercase tracking-widest hover:border-slate-300 hover:text-slate-600 hover:bg-slate-50 transition-all flex items-center justify-center gap-2"
            >
              <Plus size={14} /> Nieuw Profiel Toevoegen
            </button>
          </div>
        </div>
      )}

      {/* Righthand User Detail Slider */}
      {selectedUser && (
        <div className="fixed inset-0 z-[100] flex justify-end">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-slate-900/20 backdrop-blur-sm transition-opacity animate-fadeIn"
            onClick={() => setSelectedUser(null)}
          />
          
          {/* Drawer */}
          <div className="relative w-full max-w-md bg-white h-full shadow-2xl flex flex-col transform transition-transform duration-300 translate-x-0 overflow-y-auto animate-slideInRight">
            {/* Header */}
            <div className="p-6 border-b border-slate-100 flex items-start justify-between bg-slate-50/50 sticky top-0 z-10 backdrop-blur-md">
              <div className="flex gap-4 items-center">
                 <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-lg font-black border shadow-sm ${selectedUser.status === 'Active' ? 'bg-white border-slate-100 text-slate-700' : 'bg-slate-100 border-slate-200 text-slate-400'}`}>
                    {selectedUser.client_logo ? (
                      <img src={selectedUser.client_logo} alt={selectedUser.client_name} className="w-full h-full object-contain p-2" />
                    ) : (
                      selectedUser.client_name ? selectedUser.client_name.charAt(0) : getInitials(selectedUser.name)
                    )}
                 </div>
                 <div>
                    <h2 className="text-lg font-black text-slate-900 flex items-center gap-2">
                      {selectedUser.name}
                      <div className={`w-2 h-2 rounded-full ${selectedUser.status === 'Active' ? 'bg-emerald-500' : 'bg-slate-300'}`} title={selectedUser.status}></div>
                    </h2>
                    <p className="text-sm text-slate-500 font-medium">{selectedUser.email}</p>
                    {/* SSO Providers */}
                    {selectedUser.providers && selectedUser.providers.length > 0 && (
                      <div className="flex items-center gap-1.5 mt-2">
                        {selectedUser.providers.includes('azure') && (
                            <span className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest text-[#00a4ef] bg-[#00a4ef]/10 px-2 py-0.5 rounded-md">Microsoft</span>
                        )}
                        {selectedUser.providers.includes('google') && (
                            <span className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest text-[#ea4335] bg-[#ea4335]/10 px-2 py-0.5 rounded-md">Google</span>
                        )}
                        {selectedUser.providers.includes('email') && (
                            <span className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest text-slate-500 bg-slate-100 px-2 py-0.5 rounded-md">Email</span>
                        )}
                      </div>
                    )}
                 </div>
              </div>
              <button 
                onClick={() => setSelectedUser(null)}
                className="w-8 h-8 flex items-center justify-center bg-white border border-slate-200 text-slate-400 hover:text-slate-900 hover:bg-slate-100 hover:border-slate-300 rounded-full transition-colors shadow-sm"
              >
                <X size={16} />
              </button>
            </div>

            <div className="flex-1 p-6 flex flex-col gap-6">
              {/* Context Block */}
              <div className="bg-blue-50/50 border border-blue-100 rounded-2xl p-5 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-blue-100 rounded-full blur-3xl opacity-50 -translate-y-1/2 translate-x-1/4"></div>
                <div className="relative z-10">
                  <h3 className="text-[10px] font-black uppercase tracking-widest text-blue-500 mb-2 flex items-center gap-1.5">
                    <Briefcase size={12} /> Zakelijk Profiel
                  </h3>
                  
                  {selectedUser.client_name ? (
                    <div>
                      <div className="text-sm font-medium text-slate-600 mb-1">
                        Contactpersoon van:
                      </div>
                      <div className="text-lg font-black text-slate-900 flex items-center gap-2">
                        <Building2 size={20} className="text-blue-500" /> {selectedUser.client_name}
                      </div>
                      <div className="flex flex-wrap gap-2 mt-3">
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 text-[9px] font-black uppercase tracking-widest rounded-md">
                          {selectedUser.client_type === 'Agency' ? 'Partner / Bureau' : 'Eindklant'}
                        </span>
                        {selectedUser.client_industry && (
                          <span className="px-2 py-1 bg-white border border-blue-100 text-slate-500 text-[9px] font-black uppercase tracking-widest rounded-md">
                            {selectedUser.client_industry}
                          </span>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="text-sm font-medium text-slate-500 mb-3">
                        Deze gebruiker is momenteel nog niet gekoppeld aan een organisatie in de database.
                      </div>
                      <button 
                        onClick={() => { setSelectedUser(null); startEdit(selectedUser); }}
                        className="px-4 py-2 bg-white text-blue-600 hover:bg-blue-50 border border-blue-200 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center gap-2 transition-all shadow-sm"
                      >
                        <Building2 size={14} /> Nu Koppelen
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Roles & Permissions */}
              <div className="space-y-3">
                <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-400 border-b border-slate-100 pb-2">Modules & Rechten</h3>
                
                <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1.5 bg-slate-900 text-white rounded-lg text-[10px] font-black uppercase tracking-wider">
                      {selectedUser.role === 'CLIENT_RECRUITER'
                        ? 'REVIEWER (Lezen/Beoordelen)'
                        : selectedUser.role === 'CLIENT' ? 'ADMIN (Beheerder / Hoofdcontact)' : selectedUser.role.replace('CLIENT_', '')
                      }
                    </span>
                    {user?.role === UserRole.ADMIN && selectedUser.role === UserRole.ADMIN && (
                       <span className="px-3 py-1.5 bg-purple-50 text-purple-600 border border-purple-100 rounded-lg text-[10px] font-black uppercase tracking-wider">
                         Werftools Portaalbeheerder
                       </span>
                    )}
                </div>

                {selectedUser.visibleTabs && selectedUser.visibleTabs.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {selectedUser.visibleTabs.map(tab => {
                      const label = AVAILABLE_PAGES.find(p => p.id === tab)?.label || tab;
                      return (
                        <span key={tab} className="px-2 py-1 bg-slate-50 text-slate-500 border border-slate-100 rounded text-[9px] font-bold uppercase tracking-wider">
                          <CheckCircle2 size={10} className="inline mr-1 text-emerald-400" /> {label}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Action Board */}
              <div className="mt-auto space-y-3 pt-6 border-t border-slate-100">
                 <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">Administratieve Acties</h3>
                 
                 {selectedUser.status === 'Active' && (
                  <button 
                    onClick={() => handleLoginAs(selectedUser)}
                    className="w-full py-3 px-4 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 hover:text-emerald-800 rounded-xl text-[11px] font-black uppercase tracking-widest flex items-center justify-between transition-colors outline-none shrink-0"
                  >
                    <span className="flex items-center gap-2"><ArrowRight size={16} /> Inloggen als {selectedUser.name.split(' ')[0]}</span>
                  </button>
                 )}

                 <button 
                    onClick={() => { setSelectedUser(null); startEdit(selectedUser); }}
                    className="w-full py-3 px-4 bg-slate-50 text-slate-700 hover:bg-slate-100 hover:text-slate-900 rounded-xl text-[11px] font-black uppercase tracking-widest flex items-center justify-between transition-colors border border-slate-200 outline-none shrink-0"
                  >
                    <span className="flex items-center gap-2"><Edit3 size={16} /> Profiel Bewerken</span>
                 </button>

                 {user?.id !== selectedUser.id && (
                  <div className={`grid ${selectedUser.status === 'Archived' ? 'grid-cols-1' : 'grid-cols-2'} gap-3 pt-2`}>
                    <button 
                        onClick={() => handleToggleSuspend(selectedUser)}
                        className={`py-3 px-2 rounded-xl text-[9px] font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-colors border outline-none shrink-0 ${['Suspended', 'Archived'].includes(selectedUser.status) ? 'bg-amber-50 text-amber-600 border-amber-200 hover:bg-amber-100' : 'bg-white text-amber-500 border-slate-200 hover:border-amber-300 hover:bg-amber-50'}`}
                      >
                        {selectedUser.status === 'Archived' ? (
                          <><Play size={14} /> Dearchiveren</>
                        ) : selectedUser.status === 'Suspended' ? (
                          <><Play size={14} /> Account Activeren</>
                        ) : (
                          <><Ban size={14} /> Toegang Pauzeren</>
                        )}
                    </button>

                    {selectedUser.status !== 'Archived' && (
                      <button 
                          onClick={() => handleInitiateDelete(selectedUser)}
                          className="py-3 px-2 bg-white text-red-500 hover:bg-red-50 hover:text-red-700 hover:border-red-200 border border-slate-200 rounded-xl text-[9px] font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-colors outline-none shrink-0"
                        >
                          <Trash2 size={14} /> Account Archiveren
                      </button>
                    )}
                  </div>
                 )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Type-to-Confirm Deletion Modal */}
      {deleteConfirmationUser && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white w-full max-w-md rounded-[2rem] shadow-2xl overflow-hidden p-8 animate-scaleIn relative">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 bg-red-50 text-red-600 rounded-2xl flex items-center justify-center shrink-0 border border-red-100 shadow-sm">
                <Trash2 size={24} />
              </div>
              <div>
                <h2 className="text-xl font-black text-slate-900 tracking-tight leading-tight">Archiveren Bevestigen</h2>
                <p className="text-xs font-bold text-red-500 uppercase tracking-widest mt-1">Gevaarlijke Actie</p>
              </div>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 mb-8">
               <p className="text-sm text-slate-700 font-medium mb-4 leading-relaxed">
                 Weet je zeker dat je het account van <strong className="text-slate-900">{deleteConfirmationUser.name}</strong> wilt archiveren? De gebruiker kan niet meer inloggen, maar data blijft bewaard.
               </p>
               <div className="space-y-2">
                 <p className="text-[10px] font-black uppercase tracking-widest text-[#e51b23] mb-1">
                   Typ de voornaam om te bevestigen: <strong className="text-slate-900 bg-white px-2 py-0.5 border border-slate-200 rounded shadow-sm">{deleteConfirmationUser.name.split(' ')[0]}</strong>
                 </p>
                 <input 
                   type="text"
                   value={deleteInput}
                   onChange={e => setDeleteInput(e.target.value)}
                   onKeyDown={e => {
                      if (e.key === 'Enter' && deleteInput.trim().toLowerCase() === deleteConfirmationUser.name.split(' ')[0].toLowerCase()) {
                          confirmDelete();
                      }
                   }}
                   className="w-full p-3 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-900 outline-none focus:border-[#e51b23] focus:ring-1 focus:ring-[#e51b23] transition-all"
                   placeholder={deleteConfirmationUser.name.split(' ')[0]}
                   autoFocus
                 />
               </div>
            </div>

            <div className="flex gap-3 mt-8">
              <button 
                onClick={() => { setDeleteConfirmationUser(null); setDeleteInput(''); }}
                className="flex-1 py-3.5 bg-slate-50 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-xl text-xs font-black uppercase tracking-widest transition-colors disabled:opacity-50"
                disabled={isDeleting}
              >
                Annuleren
              </button>
              <button 
                onClick={confirmDelete}
                disabled={deleteInput.trim().toLowerCase() !== deleteConfirmationUser.name.split(' ')[0].toLowerCase() || isDeleting}
                className="flex-1 py-3.5 bg-[#e51b23] text-white rounded-xl text-xs font-black uppercase tracking-widest transition-all hover:bg-black disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg"
              >
                {isDeleting ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                Archiveren
              </button>
            </div>
          </div>
        </div>
      )}

      </div>
    </div>
  );
};

export default UserManagement;
